import { useState, useMemo } from 'react'
import { assignmentApi, usersApi, leadsApi } from '../../api/endpoints'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import Button from '../../components/ui/Button'
import Badge from '../../components/ui/Badge'
import EmptyState from '../../components/ui/EmptyState'
import LoadingState from '../../components/ui/LoadingState'
import { Users, Target, Clock, Shield, RefreshCw, UserCheck } from 'lucide-react'
import { fmtDate } from '../../utils/date'
import { useScoringSegments } from '../../hooks/usePipelineStages'

function StatCard({ icon: Icon, label, value, sub, color }: {
  icon: any; label: string; value: string | number; sub?: string; color: string
}) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${color}`}>
          <Icon size={18} className="text-white" />
        </div>
        <span className="text-sm text-gray-500">{label}</span>
      </div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
    </div>
  )
}

function CapacityBar({ used, total }: { used: number; total: number }) {
  const pct = total > 0 ? Math.round((used / total) * 100) : 0
  const color = pct > 90 ? 'bg-red-500' : pct > 70 ? 'bg-amber-500' : 'bg-green-500'
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color} transition-all`} style={{ width: `${Math.min(pct, 100)}%` }} />
      </div>
      <span className="text-xs font-medium text-gray-600 w-10 text-right">{pct}%</span>
    </div>
  )
}

const SEGMENT_COLOR_FALLBACK: Record<string, string> = {
  HOT: '#ef4444', WARM: '#f59e0b', COLD: '#3b82f6',
}
const SEGMENT_SCORE_FALLBACK = [
  { segment: 'HOT', min_score: 75 },
  { segment: 'WARM', min_score: 45 },
  { segment: 'COLD', min_score: 0 },
]

export default function TeamWorkloadPage() {
  const queryClient = useQueryClient()
  const [assigningLeadId, setAssigningLeadId] = useState<number | null>(null)

  const scoringSegments = useScoringSegments()
  const segmentColorMap = useMemo(() => {
    if (scoringSegments.length > 0) {
      return scoringSegments.reduce<Record<string, string>>((acc, s) => {
        if (s.color) acc[s.segment] = s.color
        return acc
      }, {})
    }
    return SEGMENT_COLOR_FALLBACK
  }, [scoringSegments])

  const scoreThresholds = useMemo(() => {
    if (scoringSegments.length > 0) return scoringSegments
    return SEGMENT_SCORE_FALLBACK
  }, [scoringSegments])

  const getScoreColor = (score: number): string => {
    for (const seg of scoreThresholds) {
      if (score >= seg.min_score) return segmentColorMap[seg.segment] || '#6b7280'
    }
    return '#3b82f6'
  }

  // Fetch workload data
  const { data: workloadData, isLoading: workloadLoading } = useQuery({
    queryKey: ['assignment', 'workload'],
    queryFn: () => assignmentApi.getWorkload(),
  })

  // Fetch queue
  const { data: queueData, isLoading: queueLoading } = useQuery({
    queryKey: ['assignment', 'queue'],
    queryFn: () => assignmentApi.getQueue({ limit: 50 }),
  })

  // Fetch SLA dashboard
  const { data: slaData } = useQuery({
    queryKey: ['assignment', 'sla', 'dashboard'],
    queryFn: () => assignmentApi.getSlaStatus(),
  })

  // Fetch users for assignment dropdown
  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.list(),
  })

  // SLA check mutation
  const slaMutation = useMutation({
    mutationFn: () => assignmentApi.checkSla(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assignment', 'sla'] })
      queryClient.invalidateQueries({ queryKey: ['assignment', 'workload'] })
    },
  })

  // Auto-assign mutation
  const autoAssignMutation = useMutation({
    mutationFn: () => assignmentApi.autoAssign(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assignment'] })
    },
  })

  // Assign single lead mutation
  const assignLeadMutation = useMutation({
    mutationFn: ({ leadId, userId }: { leadId: number; userId: number }) =>
      leadsApi.assignLead(leadId, { assigned_to: userId, reason: 'Manual assignment from Team Workload' }),
    onSuccess: () => {
      setAssigningLeadId(null)
      queryClient.invalidateQueries({ queryKey: ['assignment'] })
    },
  })

  // Toggle availability mutation
  const toggleAvailabilityMutation = useMutation({
    mutationFn: ({ userId, isAvailable }: { userId: number; isAvailable: boolean }) =>
      usersApi.updateAvailability(userId, { is_available: isAvailable }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assignment', 'workload'] })
    },
  })

  const workload: any[] = workloadData?.data || []
  const queue: any[] = queueData?.leads || []
  const queueTotal = queueData?.total || 0
  const sla: any = slaData?.data || {}
  const users: any[] = usersData?.users || []

  // Compute summary stats
  const totalActiveLeads = workload.reduce((sum: number, r: any) => sum + (r.active_leads || 0), 0)
  const slaCompliance = sla.compliance_percentage ?? '--'
  const avgCapacity = workload.length > 0
    ? Math.round(workload.reduce((sum: number, r: any) => {
        const cap = r.capacity || 1
        const active = r.active_leads || 0
        return sum + (active / cap) * 100
      }, 0) / workload.length)
    : 0

  if (workloadLoading) {
    return <LoadingState message="Loading team workload..." />
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Team Workload</h1>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => slaMutation.mutate()}
            loading={slaMutation.isPending}
          >
            <Shield size={15} />
            Check SLA
          </Button>
          <Button
            size="sm"
            onClick={() => autoAssignMutation.mutate()}
            loading={autoAssignMutation.isPending}
          >
            <RefreshCw size={15} />
            Auto-Assign Queue
          </Button>
        </div>
      </div>

      {/* Auto-assign result */}
      {autoAssignMutation.isSuccess && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
          Assigned {(autoAssignMutation.data as any)?.data?.assigned || 0} leads.
          {(autoAssignMutation.data as any)?.data?.skipped > 0 && (
            <span className="ml-1">Skipped {(autoAssignMutation.data as any).data.skipped}.</span>
          )}
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard icon={Target} label="Total Active Leads" value={totalActiveLeads} color="bg-blue-500" />
        <StatCard icon={Users} label="Unassigned Leads" value={queueTotal} color="bg-amber-500" sub="In assignment queue" />
        <StatCard icon={Shield} label="SLA Compliance" value={typeof slaCompliance === 'number' ? `${slaCompliance}%` : slaCompliance} color="bg-green-500" />
        <StatCard icon={Clock} label="Team Capacity Used" value={`${avgCapacity}%`} color="bg-purple-500" sub={`${workload.length} reps`} />
      </div>

      {/* Rep Workload */}
      <div className="mb-8">
        <div className="bg-white rounded-xl border border-gray-200 px-6 py-4 mb-3 md:mb-0 md:rounded-b-none md:border-b-0">
          <h2 className="text-lg font-semibold text-gray-900">Rep Workload</h2>
          <p className="text-xs text-gray-400 mt-0.5">{workload.length} team members</p>
        </div>

        {workload.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 md:rounded-t-none md:border-t">
            <EmptyState
              icon={Users}
              title="No team members found"
              description="No reps with workload data are available yet."
            />
          </div>
        ) : (
          <>
            {/* Mobile cards */}
            <div className="md:hidden space-y-3">
              {workload.map((rep: any) => {
                const capacity = rep.capacity || 1
                const active = rep.active_leads || 0
                return (
                  <div key={rep.user_id} className="bg-white rounded-xl border border-gray-200 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="font-semibold text-gray-900">{rep.full_name || rep.username || `User ${rep.user_id}`}</div>
                      <div className="flex items-center gap-2">
                        <Badge variant={rep.role === 'manager' ? 'purple' : rep.role === 'admin' ? 'danger' : 'info'}>
                          {rep.role || 'rep'}
                        </Badge>
                        <button
                          onClick={() => toggleAvailabilityMutation.mutate({
                            userId: rep.user_id,
                            isAvailable: !rep.is_available,
                          })}
                          className="flex items-center gap-1"
                        >
                          <span className={`w-2.5 h-2.5 rounded-full ${rep.is_available ? 'bg-green-500' : 'bg-gray-300'}`} />
                          <span className="text-xs text-gray-500">{rep.is_available ? 'Online' : 'Offline'}</span>
                        </button>
                      </div>
                    </div>

                    <div className="mb-3">
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-gray-500">Capacity</span>
                        <span className="font-semibold text-gray-700">{active} / {capacity} leads</span>
                      </div>
                      <CapacityBar used={active} total={capacity} />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-xs text-gray-500">Conv. Rate</p>
                        <p className="text-sm font-medium text-gray-700">
                          {rep.conversion_rate_30d != null ? `${(rep.conversion_rate_30d * 100).toFixed(1)}%` : '--'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Avg Response</p>
                        <p className="text-sm font-medium text-gray-700">
                          {rep.avg_response_time_minutes != null ? `${Math.round(rep.avg_response_time_minutes)} min` : '--'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">SLA</p>
                        <p className="text-sm font-medium">
                          {rep.sla_compliance_pct != null ? (
                            <span className={rep.sla_compliance_pct >= 90 ? 'text-green-600' : rep.sla_compliance_pct >= 70 ? 'text-amber-600' : 'text-red-600'}>
                              {rep.sla_compliance_pct}%
                            </span>
                          ) : '--'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Specializations</p>
                        <div className="flex flex-wrap gap-1 mt-0.5">
                          {(rep.product_specializations || []).map((spec: string) => (
                            <Badge key={spec} variant="default">{spec}</Badge>
                          ))}
                          {(!rep.product_specializations || rep.product_specializations.length === 0) && (
                            <span className="text-xs text-gray-400">--</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Desktop table */}
            <div className="hidden md:block bg-white rounded-xl border border-gray-200 md:rounded-t-none md:border-t">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 text-left">
                      <th className="px-6 py-3 font-medium text-gray-500 text-xs uppercase tracking-wider">Rep</th>
                      <th className="px-6 py-3 font-medium text-gray-500 text-xs uppercase tracking-wider">Role</th>
                      <th className="px-6 py-3 font-medium text-gray-500 text-xs uppercase tracking-wider w-48">Capacity</th>
                      <th className="px-6 py-3 font-medium text-gray-500 text-xs uppercase tracking-wider">Conv. Rate</th>
                      <th className="px-6 py-3 font-medium text-gray-500 text-xs uppercase tracking-wider">Avg Response</th>
                      <th className="px-6 py-3 font-medium text-gray-500 text-xs uppercase tracking-wider">SLA</th>
                      <th className="px-6 py-3 font-medium text-gray-500 text-xs uppercase tracking-wider">Available</th>
                      <th className="px-6 py-3 font-medium text-gray-500 text-xs uppercase tracking-wider">Specializations</th>
                    </tr>
                  </thead>
                  <tbody>
                    {workload.map((rep: any) => {
                      const capacity = rep.capacity || 1
                      const active = rep.active_leads || 0
                      const capPct = Math.round((active / capacity) * 100)
                      const capColor = capPct > 90 ? 'text-red-600' : capPct > 70 ? 'text-amber-600' : 'text-green-600'

                      return (
                        <tr key={rep.user_id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-3">
                            <div className="font-medium text-gray-900">{rep.full_name || rep.username || `User ${rep.user_id}`}</div>
                          </td>
                          <td className="px-6 py-3">
                            <Badge variant={rep.role === 'manager' ? 'purple' : rep.role === 'admin' ? 'danger' : 'info'}>
                              {rep.role || 'rep'}
                            </Badge>
                          </td>
                          <td className="px-6 py-3">
                            <div className="space-y-1">
                              <div className="flex items-center justify-between">
                                <span className={`text-xs font-semibold ${capColor}`}>{active} / {capacity}</span>
                              </div>
                              <CapacityBar used={active} total={capacity} />
                            </div>
                          </td>
                          <td className="px-6 py-3 text-gray-700">
                            {rep.conversion_rate_30d != null ? `${(rep.conversion_rate_30d * 100).toFixed(1)}%` : '--'}
                          </td>
                          <td className="px-6 py-3 text-gray-700">
                            {rep.avg_response_time_minutes != null ? `${Math.round(rep.avg_response_time_minutes)} min` : '--'}
                          </td>
                          <td className="px-6 py-3">
                            {rep.sla_compliance_pct != null ? (
                              <span className={`font-medium ${rep.sla_compliance_pct >= 90 ? 'text-green-600' : rep.sla_compliance_pct >= 70 ? 'text-amber-600' : 'text-red-600'}`}>
                                {rep.sla_compliance_pct}%
                              </span>
                            ) : '--'}
                          </td>
                          <td className="px-6 py-3">
                            <button
                              onClick={() => toggleAvailabilityMutation.mutate({
                                userId: rep.user_id,
                                isAvailable: !rep.is_available,
                              })}
                              className="flex items-center gap-1.5 group"
                              title={rep.is_available ? 'Click to mark unavailable' : 'Click to mark available'}
                            >
                              <span className={`w-2.5 h-2.5 rounded-full transition-colors ${rep.is_available ? 'bg-green-500' : 'bg-gray-300'}`} />
                              <span className="text-xs text-gray-500 group-hover:text-gray-700">
                                {rep.is_available ? 'Online' : 'Offline'}
                              </span>
                            </button>
                          </td>
                          <td className="px-6 py-3">
                            <div className="flex flex-wrap gap-1">
                              {(rep.product_specializations || []).map((spec: string) => (
                                <Badge key={spec} variant="default">{spec}</Badge>
                              ))}
                              {(!rep.product_specializations || rep.product_specializations.length === 0) && (
                                <span className="text-xs text-gray-400">--</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Assignment Queue */}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Assignment Queue</h2>
            <p className="text-xs text-gray-400 mt-0.5">{queueTotal} unassigned leads</p>
          </div>
          {queue.length > 0 && (
            <Button
              size="sm"
              onClick={() => autoAssignMutation.mutate()}
              loading={autoAssignMutation.isPending}
            >
              <RefreshCw size={14} />
              Auto-Assign All
            </Button>
          )}
        </div>

        {queueLoading ? (
          <LoadingState message="Loading queue..." />
        ) : queue.length === 0 ? (
          <EmptyState
            icon={UserCheck}
            title="Queue is empty"
            description="All leads have been assigned. New unassigned leads will appear here."
          />
        ) : (
          <>
          {/* Mobile cards */}
          <div className="md:hidden divide-y divide-gray-100">
            {queue.map((lead: any) => (
              <div key={lead.id} className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="font-semibold text-gray-900">{lead.name}</div>
                    {lead.company && <div className="text-xs text-gray-400">{lead.company}</div>}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold" style={{ color: getScoreColor(lead.score || 0) }}>
                      {lead.score ?? '--'}
                    </span>
                    {lead.quality_segment ? (
                      <span
                        className="px-2 py-0.5 rounded text-[10px] font-bold text-white"
                        style={{ backgroundColor: segmentColorMap[lead.quality_segment] || '#6b7280' }}
                      >
                        {lead.quality_segment}
                      </span>
                    ) : null}
                  </div>
                </div>
                <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
                  {lead.source && <span>{lead.source}</span>}
                  {lead.created_at && <span>{fmtDate(lead.created_at)}</span>}
                </div>
                {assigningLeadId === lead.id ? (
                  <div className="flex items-center gap-2">
                    <select
                      className="text-xs border border-gray-300 rounded-lg px-2 py-1.5 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 flex-1"
                      defaultValue=""
                      onChange={(e) => {
                        if (e.target.value) {
                          assignLeadMutation.mutate({ leadId: lead.id, userId: Number(e.target.value) })
                        }
                      }}
                    >
                      <option value="" disabled>Select rep...</option>
                      {users
                        .filter((u: any) => u.is_active && ['sales_rep', 'manager', 'admin'].includes(u.role))
                        .map((u: any) => (
                          <option key={u.id} value={u.id}>{u.full_name || u.username}</option>
                        ))}
                    </select>
                    <button
                      onClick={() => setAssigningLeadId(null)}
                      className="text-xs text-gray-400 hover:text-gray-600"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setAssigningLeadId(lead.id)}
                    className="w-full"
                  >
                    <UserCheck size={13} />
                    Assign
                  </Button>
                )}
              </div>
            ))}
          </div>

          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-left">
                  <th className="px-6 py-3 font-medium text-gray-500 text-xs uppercase tracking-wider">Lead</th>
                  <th className="px-6 py-3 font-medium text-gray-500 text-xs uppercase tracking-wider">Score</th>
                  <th className="px-6 py-3 font-medium text-gray-500 text-xs uppercase tracking-wider">Segment</th>
                  <th className="px-6 py-3 font-medium text-gray-500 text-xs uppercase tracking-wider">Source</th>
                  <th className="px-6 py-3 font-medium text-gray-500 text-xs uppercase tracking-wider">Created</th>
                  <th className="px-6 py-3 font-medium text-gray-500 text-xs uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody>
                {queue.map((lead: any) => (
                  <tr key={lead.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-3">
                      <div className="font-medium text-gray-900">{lead.name}</div>
                      {lead.company && <div className="text-xs text-gray-400">{lead.company}</div>}
                    </td>
                    <td className="px-6 py-3">
                      <span className="font-semibold" style={{ color: getScoreColor(lead.score || 0) }}>
                        {lead.score ?? '--'}
                      </span>
                    </td>
                    <td className="px-6 py-3">
                      {lead.quality_segment ? (
                        <span
                          className="px-2 py-0.5 rounded text-[10px] font-bold text-white"
                          style={{ backgroundColor: segmentColorMap[lead.quality_segment] || '#6b7280' }}
                        >
                          {lead.quality_segment}
                        </span>
                      ) : '--'}
                    </td>
                    <td className="px-6 py-3 text-gray-600">{lead.source || '--'}</td>
                    <td className="px-6 py-3 text-gray-500 text-xs">
                      {lead.created_at ? fmtDate(lead.created_at) : '--'}
                    </td>
                    <td className="px-6 py-3">
                      {assigningLeadId === lead.id ? (
                        <div className="flex items-center gap-2">
                          <select
                            className="text-xs border border-gray-300 rounded-lg px-2 py-1.5 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                            defaultValue=""
                            onChange={(e) => {
                              if (e.target.value) {
                                assignLeadMutation.mutate({ leadId: lead.id, userId: Number(e.target.value) })
                              }
                            }}
                          >
                            <option value="" disabled>Select rep...</option>
                            {users
                              .filter((u: any) => u.is_active && ['sales_rep', 'manager', 'admin'].includes(u.role))
                              .map((u: any) => (
                                <option key={u.id} value={u.id}>{u.full_name || u.username}</option>
                              ))}
                          </select>
                          <button
                            onClick={() => setAssigningLeadId(null)}
                            className="text-xs text-gray-400 hover:text-gray-600"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => setAssigningLeadId(lead.id)}
                        >
                          <UserCheck size={13} />
                          Assign
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}
      </div>
    </div>
  )
}
